import { memo as _$memo } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { A, useNavigate, useSearchParams } from "@solidjs/router";
import { createSignal, onCleanup, onMount, Show } from "solid-js";
import { dogfightDebrief, missionDebrief, playerFlightOf } from "@red-opus/engine";
import { browserSettingsStore } from "../audio/settings.ts";
import { markBootHealthy, markBootProgress } from "../offline/boot-health.ts";
import { skyTreatmentOverride, SKY_TREATMENT_PARAM } from "../flight/effects-settings.ts";
import { FlightEscMenu } from "../flight/EscMenuOverlay.tsx";
import { createEscMenuSession } from "../flight/esc-menu-session.ts";
import { createDashCanvasFeed } from "../flight/cockpit-dash-quad.ts";
import { cockpit3dDrawn } from "../flight/scene-camera.ts";
import { createLiveSettings } from "../flight/live-settings.ts";
import { createPersistedFlightSettings } from "../flight/persisted-settings.ts";
import { currentRealism } from "../mission/realism-settings.ts";
import { DebriefScreen } from "../flight/DebriefScreen.tsx";
import { ENCOUNTER_FLIGHT, FIRST_ENCOUNTER, PLAYER_ID } from "../flight/encounter-setup.ts";
import { FlightSurface } from "../flight/FlightSurface.tsx";
import { createBootStatus } from "../flight/boot-status.ts";
import { flightRegionChips } from "../flight/fly-region.ts";
import { cameraModeForCommand, createFlightScene, DEFAULT_CAMERA_MODE, IDLE_COCKPIT_VIEW } from "../flight/flight-scene.ts";
import { createFlightInputSession } from "../flight/input-session.ts";
import { idleMissionHud } from "../flight/mission-hud.ts";
import { describeFlight, initialHud } from "../flight/sim-runner.ts";
import { TIME_COMPRESSION_FACTOR } from "../flight/time-compression.ts";
import { browserCareerStore } from "../career/career-store.ts";
import { resolveFlyTarget } from "../flight/fly-target.ts";
import { createFlightRecording } from "../vcr/flight-recording.ts";
import { FlightRecorderOverlay } from "../vcr/FlightRecorderOverlay.tsx";
import { createBestMissionRecording } from "../missions/best-mission-recording.ts";
import { BestMissionOverlay } from "../missions/BestMissionOverlay.tsx";
import { FlightMap } from "../map/MapOverlay.tsx";
import { createMapSession } from "../map/map-session.ts";
const CAMERA_LABELS = {
  cockpit: "3 · COCKPIT VIEW",
  chase: "1 · CHASE CAMERA",
  flyBy: "2 · FLY-BY CAMERA"
};

/** How many message-line entries stay on screen. */
const MESSAGE_HISTORY = 3;

/**
 * The mission's own aeroplane and Realism Panel, so the corner chip and the
 * gauges read correctly for the whole frame before the scene's first `onHud`.
 */
function flightOptionsFor(mission) {
  if (!mission) return ENCOUNTER_FLIGHT;
  const player = playerFlightOf(mission);
  return {
    ...ENCOUNTER_FLIGHT,
    aircraft: player.aircraft,
    tier: mission.tier,
    realism: mission.realism,
    // The mission's briefed wind word is the sim's weather (AD-007k).
    weather: mission.weather.wind,
    altitudeM: player.position.y
  };
}

/**
 * `createFlightScene`'s per-flight options: the mission, the live audio
 * settings, the career pilot's paint, and the two Realism Panel toggles this
 * lane wires up straight from the panel rather than through the mission spec
 * — SUN BLIND SPOT and REAL NAVIGATION are presentation, not sim state
 * (`realism-settings.ts`'s docstring). One place, so `onMount` stays about
 * wiring the scene up rather than building its arguments.
 */
function sceneOptionsFor(mission, audio, effects, stick, target, realismExtra, /** The route's own query, which `resolveFlyTarget` has already read the mission out of. */
search, /** 3D COCKPIT's shared canvas feed — the panel composes into it, the scene's quad draws it. */
dashFeed, /** Where the pilot last left the kill-camera window; `null` is its default corner. */
killCamPlacement, /** AD-021's BAIL OUT, as stored. `live.applyBail` keeps the running scene in step after this. */
bail) {
  // `?treatment=measured` and friends — the debug hatch onto the byte-comparison
  // sky reference (`effects-settings.ts`). Absent from an ordinary URL, so an
  // ordinary flight takes the ENHANCED SKY setting.
  const skyOverride = skyTreatmentOverride(search[SKY_TREATMENT_PARAM]);
  return {
    audio,
    effects,
    dashFeed,
    stickShaping: stick.shaping,
    bailEnabled: bail.enabled,
    mission,
    sunBlindSpot: realismExtra.sunBlindSpot,
    realNavigation: realismExtra.realNavigation,
    killCamPlacement,
    ...(skyOverride ? {
      skyOverride
    } : {}),
    ...(target.playerPaint ? {
      playerPaint: target.playerPaint
    } : {}),
    // A career pilot flies on a real date, and the ENHANCED night sky puts the
    // stars and the moon where they were on it (`night-sky-look.ts`).
    ...(target.date ? {
      careerDate: target.date
    } : {})
  };
}

/**
 * The HUD's **standing** corner chips: the region's once it has loaded, and the
 * time-compression factor while it is running. Both are live state — where the
 * aeroplane is, and whether the clock is being run fast — which is what earns a
 * permanent place here.
 *
 * The flight's realism settings are deliberately not among them. They cannot
 * change during a sortie, so they travel separately as `realismChips` and are
 * announced rather than displayed (`hud-format.ts`'s `realismChipsVisible`,
 * `docs/engineering/hud-chrome.md` §2).
 *
 * The compression chip is a port addition. The original's own feedback is the
 * `RB.EXE` status message ("TIME COMPRESS IS ON.", carried on `onMessages`);
 * the chip keeps the factor visible for as long as it is running rather than
 * for the few seconds the message scrolls by.
 */
function hudChips(region, timeCompressed) {
  return [...(region ? flightRegionChips(region) : []), ...(timeCompressed ? [`TIME COMPRESS ${TIME_COMPRESSION_FACTOR}X`] : [])];
}

/** The HUD's combat half before a scene exists: nothing seen, nothing decided. */
const NO_CONTACT = {
  enemy: undefined,
  status: "active",
  banner: undefined,
  airframe: 1,
  wounded: false,
  burning: false,
  bombs: undefined,
  hitsScored: 0,
  hitsTaken: 0,
  gunHitsTaken: 0
};

/**
 * `DiscreteCommand` has 10 members: 4 camera commands (`cameraModeForCommand`),
 * `escMenu`, `pause`, `map`, `preferences`, `timeCompress`, and `muteAudio` —
 * whatever falls through every check below is always the last one. A factory rather than a method on
 * `Fly()` itself so the function stays short enough for the file-size gate;
 * `scene` is read through a getter because `Fly()`'s own `scene` binding is
 * a plain `let`, reassigned once `onMount`'s `createFlightScene` returns.
 *
 * **The map and the PREFERENCES panel pause the flight, and `P`, `M` and `O`
 * do not fight over it.** The original settles nothing here — it has no
 * in-flight map and no in-flight options screen to imitate — so the port takes
 * the single-player reading: reading a map, or setting the sky up, is
 * something a pilot does with his eyes off the sky, and leaving the sim running
 * under a full-screen sheet would just get him shot down by an enemy he cannot
 * see. The sim is therefore stopped while *any* of the three is asking for it,
 * which is what `pauseWanted` computes — one place, so a new full-screen
 * surface cannot resume the flight out from under an older one. (`/duel` mounts
 * neither the map nor a pause: its runner is `packages/net`'s lockstep one and
 * pausing one peer desyncs both.)
 */
function createRunCommand(deps) {
  /** Everything that wants the sim stopped, with the one flag the caller just changed overridden. */
  const pauseWanted = over => (over.paused ?? deps.paused()) || (over.map ?? deps.mapOpen()) || (over.preferences ?? deps.preferencesOpen()) || (over.escMenu ?? deps.escMenuOpen());
  return command => {
    const mode = cameraModeForCommand(command, deps.cameraMode());
    if (mode) {
      deps.setCameraMode(mode);
      deps.scene()?.setCameraMode(mode);
      return;
    }
    if (command === "map") {
      const next = !deps.mapOpen();
      deps.setMapOpen(next);
      deps.scene()?.setPaused(pauseWanted({
        map: next
      }));
      return;
    }
    if (command === "preferences") {
      const next = !deps.preferencesOpen();
      deps.setPreferencesOpen(next);
      deps.scene()?.setPaused(pauseWanted({
        preferences: next
      }));
      return;
    }
    if (command === "escMenu") {
      const next = !deps.escMenuOpen();
      deps.setEscMenuOpen(next);
      deps.scene()?.setPaused(pauseWanted({
        escMenu: next
      }));
      return;
    }
    if (command === "pause") {
      const next = !deps.paused();
      deps.setPaused(next);
      deps.scene()?.setPaused(pauseWanted({
        paused: next
      }));
      return;
    }
    if (command === "timeCompress") {
      // A request, not a guaranteed toggle: the scene refuses (and reports
      // "NOT AVAILABLE NOW" through `onMessages`) while a contact is close,
      // and `onTimeCompression` is what actually flips this signal — see
      // `time-compression.ts`.
      deps.scene()?.setTimeCompression(!deps.timeCompressed());
      return;
    }
    deps.applyAudioSettings({
      ...deps.audioSettings(),
      muted: !deps.audioSettings().muted
    });
  };
}

/**
 * The whole callback set `createFlightScene` is wired to, built once here
 * rather than inline in `Fly()` for the same file-size reason `createRunCommand`
 * is split out. `onMissionEnd`/`onDogfightEnd` are the same debrief, from the
 * two mutually exclusive ways a flight ends (`flight-scene.ts`'s `isDogfight`).
 */
function flightSceneCallbacks(deps) {
  const debrief = report => {
    deps.setDebrief(report);
    deps.target.credit(report);
    // BEST MISSIONS credits the same debriefing, independently of whatever
    // `target.credit` just did with it — a no-op there (career, bare dogfight)
    // is a no-op here too, `createBestMissionRecording`'s own eligibility gate.
    deps.bestMission.credit(report);
  };
  return {
    onStatus: (next, message) => {
      deps.boot.report(next, message);
      // The scene is flyable: clear the offline heartbeat's failure counter
      // (`boot-health.ts`). `/fly` hanging on "Starting engine…" with the page
      // otherwise alive is the 2026-08-25 skew this watches for.
      if (next === "ready") markBootHealthy();
    },
    onBootProgress: progress => {
      // Every phase report is also the heartbeat's "still moving" signal, so a
      // boot that is merely slow — a weak GPU in `precompile`, a big airframe on
      // a thin link — is never mistaken for one wedged by a stale cache
      // (`boot-health.ts`). The bar and the watchdog read the same evidence.
      markBootProgress();
      deps.boot.reportProgress(progress);
    },
    onHud: deps.setHud,
    onCombat: deps.setCombat,
    onCockpitView: deps.setCockpitView,
    onTimeCompression: deps.setTimeCompressed,
    onKillCam: deps.setKillCamOpen,
    onMission: deps.setMissionHud,
    onMissionEnd: run => debrief(missionDebrief(run)),
    onDogfightEnd: encounter => debrief(dogfightDebrief(encounter, PLAYER_ID)),
    onRegion: deps.setRegion,
    onFlown: (inputs, ticks, encounter) => {
      deps.recording.onFlown(inputs, ticks, encounter);
      deps.trackPlayer(encounter);
    },
    onMessages: deps.setMessages,
    pressedKeys: deps.input.pressedKeys,
    pointer: deps.input.pointer,
    gamepad: deps.input.gamepad,
    freeLook: deps.input.freeLook
  };
}

/**
 * What the route shows once a flight is decided: the debriefing card, and the
 * BEST MISSIONS prompt that credits the same report independently of it.
 * Renders nothing while `debrief` is `undefined`.
 *
 * Lifted out of `Fly()` for the same file-size reason `createRunCommand` and
 * `flightSceneCallbacks` above it were — the route is at the gate's ceiling and
 * this is a self-contained end-of-flight surface with no state of its own.
 */
function FlyDebrief(props) {
  const [asked, setAsked] = createSignal(false);
  return _$createComponent(Show, {
    get when() {
      return props.debrief;
    },
    children: report => [_$createComponent(DebriefScreen, {
      debrief: () => props.debrief,
      get nation() {
        return props.target.nation;
      },
      onContinue: () => {
        // Where the original puts the question: the debriefing's CONTINUE
        // leads to *"Would you like to save this mission to a tape?"*
        // (docs/game/ui-reference/dialog-save-to-tape.png), and only then
        // out. A flight already saved, or one with nothing recorded, goes
        // straight out.
        //
        // Asked **once**, whatever the answer, which is why `asked` is a
        // signal and not a re-read of `savedAs()`: declining the question
        // leaves the flight unsaved, and a CONTINUE that re-asked on that
        // basis would be a door that never opens.
        if (!asked() && !props.recording.savedAs()) {
          setAsked(true);
          props.recording.openPrompt();
          return undefined;
        }
        props.onContinue();
        return undefined;
      }
    }), _$createComponent(BestMissionOverlay, {
      get recording() {
        return props.bestMission;
      },
      get missionTitle() {
        return report().title;
      },
      get score() {
        return report().missionScore;
      }
    })]
  });
}

/**
 * `/fly` — the flight scene. The three.js/WebGPU side lives in
 * `flight-scene.ts` and the sim in `sim-runner.ts`; this route owns the DOM
 * overlay, the keyboard session, and the signals connecting them.
 *
 * Three ways in, one scene (`fly-target.ts` resolves which): `/fly` on its own
 * is the instant 1v1 dogfight it has always been; `/fly?m=<catalog id>&seed=<n>`
 * regenerates a single mission from the pure generators; and
 * `/fly?career=<pilot>` regenerates that pilot's next career assignment and
 * credits the debriefing to him when it is decided.
 *
 * **The renderer is chosen, not required** (`three/renderer-mode.ts`, which
 * amends AD-005): with no `navigator.gpu`, a failed adapter or device request,
 * or a driver failure remembered from an earlier boot, this route comes up on
 * `WebGPURenderer`'s WebGL2 backend and says so on the loading screen and on
 * PREFERENCES. `docs/engineering/webgl-fallback.md` records what that costs,
 * measured.
 */
/**
 * Every signal the route owns, in one place. Split out of `Fly` when the
 * function crossed the size gate at the G38 merge — the block has no
 * dependency on the sessions and commands built after it, which is what makes
 * the cut clean.
 */
function createFlySignals(mission) {
  // Always `"initializing"` now: there is no browser this route refuses to
  // start in. `three/renderer-mode.ts` answers "which backend", not "whether",
  // and a machine with no `navigator.gpu` gets the WebGL picture with
  // `RENDERER: WEBGL` under the loading bar instead of a paragraph of prose.
  const boot = createBootStatus("initializing");
  const [hud, setHud] = createSignal(initialHud(flightOptionsFor(mission)));
  const [combat, setCombat] = createSignal(NO_CONTACT);
  const [missionHud, setMissionHud] = createSignal(mission ? idleMissionHud(mission.title) : undefined);
  const [debrief, setDebrief] = createSignal(undefined);
  const [region, setRegion] = createSignal(undefined);
  const [messages, setMessages] = createSignal([]);
  // Cockpit is the spawn default (the original's primary way to fly —
  // `docs/game/manual/04-gameplay.md:673`); 1/2/3 or C still switch views.
  const [cameraMode, setCameraMode] = createSignal(DEFAULT_CAMERA_MODE);
  const [cockpitView, setCockpitView] = createSignal(IDLE_COCKPIT_VIEW);
  const [paused, setPaused] = createSignal(false);
  /** The map sheet (`M`) — open/never-opened/last fix, in one place (`map/map-session.ts`). */
  const map = createMapSession(mission?.playerFlightId ?? PLAYER_ID);
  const [timeCompressed, setTimeCompressed] = createSignal(false);
  // AD-015: the scene reports both edges of its corner window, so the overlay
  // can frame it and the controls hint can stand out of the corner it wants.
  const [killCamOpen, setKillCamOpen] = createSignal(false);
  const [hintVisible, setHintVisible] = createSignal(true);
  return {
    boot,
    hud,
    setHud,
    combat,
    setCombat,
    missionHud,
    setMissionHud,
    debrief,
    setDebrief,
    region,
    setRegion,
    messages,
    setMessages,
    cameraMode,
    setCameraMode,
    cockpitView,
    setCockpitView,
    paused,
    setPaused,
    map,
    timeCompressed,
    setTimeCompressed,
    killCamOpen,
    setKillCamOpen,
    hintVisible,
    setHintVisible
  };
}
export function Fly() {
  const [searchParams] = useSearchParams();
  const target = resolveFlyTarget(searchParams, browserCareerStore());
  const mission = target.mission;
  const briefing = describeFlight(flightOptionsFor(mission));
  const navigate = useNavigate();
  const {
    boot,
    hud,
    setHud,
    combat,
    setCombat,
    missionHud,
    setMissionHud,
    debrief,
    setDebrief,
    region,
    setRegion,
    messages,
    setMessages,
    cameraMode,
    setCameraMode,
    cockpitView,
    setCockpitView,
    paused,
    setPaused,
    map,
    timeCompressed,
    setTimeCompressed,
    killCamOpen,
    setKillCamOpen,
    hintVisible,
    setHintVisible
  } = createFlySignals(mission);
  // The three persisted settings groups, through the same helper `/duel` uses
  // (`flight/persisted-settings.ts`) — this route open-coded its own copy of
  // the load/signal/save round-trip for two of them, which is exactly the
  // duplication that helper exists to hold once.
  const settings = createPersistedFlightSettings();
  /** 3D COCKPIT's shared canvas — this route owns both ends of it (`flyChrome`'s neighbour note). */
  const dashFeed = createDashCanvasFeed();
  /** The PREFERENCES panel (`O`, or the corner button) — open/shut, and one of the three things that pauses the sim. */
  const [settingsVisible, setSettingsVisible] = createSignal(false);
  // Always on — `vcr/recording-session.ts` explains why the original's "do you
  // wish to record this mission?" is answered in advance here, and only its
  // second question, at the debriefing, is still worth asking.
  const recording = createFlightRecording(mission, FIRST_ENCOUNTER, mission?.playerFlightId ?? PLAYER_ID, mission?.title ?? "Instant dogfight");
  // BEST MISSIONS — a single mission only (`fly-target.ts`'s `credit` already
  // separates career from everything else; the eligibility test itself lives
  // in `createBestMissionRecording`, off the same two facts).
  const bestMission = createBestMissionRecording(mission, target.pilotId);
  let containerRef;
  let scene;
  // Storage and the live scene, kept in step by one path per setting
  // (`flight/live-settings.ts`).
  const live = createLiveSettings(settings, () => scene);

  // `blocked` is what stops a bound stick button firing the guns through the
  // panel it just opened; the pause alone would not (`gamepad.ts`).
  const input = createFlightInputSession({
    store: browserSettingsStore(),
    onCommand: command => runCommand(command),
    blocked: () => settingsVisible() || map.open() || escMenu.open()
  });

  /**
   * G38's board. Built before `runCommand` because that reads its open flag;
   * `close` closes over the `runCommand` declared just below, which is safe
   * because nothing calls it until the player presses something.
   */
  const escMenu = createEscMenuSession({
    hud,
    combat,
    missionHud,
    scene: () => scene,
    reload: () => flyAgain(),
    bailEnabled: () => settings.bail().enabled,
    close: () => void (escMenu.open() && runCommand("escMenu"))
  });
  const runCommand = createRunCommand({
    cameraMode,
    setCameraMode,
    paused,
    setPaused,
    mapOpen: map.open,
    setMapOpen: map.setOpen,
    preferencesOpen: settingsVisible,
    setPreferencesOpen: setSettingsVisible,
    escMenuOpen: escMenu.open,
    setEscMenuOpen: escMenu.setOpen,
    timeCompressed,
    audioSettings: settings.audio,
    applyAudioSettings: live.applyAudio,
    scene: () => scene
  });

  /**
   * Opening or closing PREFERENCES is one action wherever it comes from — the
   * `O` key, the corner button, or the panel's own ACCEPT — because it also
   * stops and restarts the simulation.
   */
  function togglePreferences() {
    runCommand("preferences");
  }

  /** A full reload rather than a signal reset: the scene owns a GPU device, and rebuilding it in place is not worth the lifecycle. */
  function flyAgain() {
    window.location.reload();
  }
  onMount(() => {
    input.open();
    onCleanup(() => input.close());
    if (!containerRef) return;
    const callbacks = flightSceneCallbacks({
      boot,
      setHud,
      setCombat,
      setCockpitView,
      setTimeCompressed,
      setKillCamOpen,
      setMissionHud,
      setDebrief,
      setRegion,
      target,
      recording,
      bestMission,
      input,
      trackPlayer: map.track,
      setMessages: incoming => setMessages(current => [...current, ...incoming].slice(-MESSAGE_HISTORY))
    });
    // SUN BLIND SPOT / REAL NAVIGATION read once here, like the mission
    // itself: the panel is not editable mid-flight, so neither needs a signal.
    const realism = currentRealism(browserSettingsStore()).extra;
    const options = sceneOptionsFor(mission, settings.audio(), settings.effects(), settings.stick(), target, realism, searchParams, dashFeed, settings.killCamPlacement(), settings.bail());
    scene = createFlightScene(containerRef, callbacks, options);
    onCleanup(() => {
      scene?.dispose();
      scene = undefined;
    });
  });
  return _$createComponent(FlightSurface, {
    containerRef: element => {
      containerRef = element;
    },
    get hud() {
      return hud();
    },
    get combat() {
      return combat();
    },
    get mission() {
      return missionHud();
    },
    get aircraft() {
      return briefing.aircraft;
    },
    get aircraftKey() {
      return flightOptionsFor(mission).aircraft;
    },
    get cockpit3d() {
      return cockpit3dDrawn(settings.effects(), flightOptionsFor(mission).aircraft);
    },
    dashFeed: dashFeed,
    get chips() {
      return hudChips(region(), timeCompressed());
    },
    get realismChips() {
      return briefing.realismChips;
    },
    get messages() {
      return messages();
    },
    get cameraLabel() {
      return CAMERA_LABELS[hud().baledOut && cameraMode() === "cockpit" ? "chase" : cameraMode()];
    },
    get cameraMode() {
      return cameraMode();
    },
    get cockpitView() {
      return cockpitView();
    },
    get paused() {
      return paused();
    },
    boot: boot,
    get audioSettings() {
      return settings.audio();
    },
    get cockpitSettings() {
      return settings.cockpit();
    },
    get onChangeAudio() {
      return live.applyAudio;
    },
    get onChangeCockpit() {
      return settings.writeCockpit;
    },
    get onChangeEffects() {
      return live.applyEffects;
    },
    get onChangeStick() {
      return live.applyStick;
    },
    get onChangeBail() {
      return live.applyBail;
    },
    onReturnToTitle: () => navigate("/"),
    get hideOutcomeBanner() {
      return debrief() !== undefined;
    },
    get gamepad() {
      return input.stick;
    },
    get settingsVisible() {
      return settingsVisible();
    },
    onToggleSettings: togglePreferences,
    onDismissSettings: togglePreferences,
    get hintVisible() {
      return hintVisible();
    },
    onDismissHint: () => setHintVisible(false),
    get killCamOpen() {
      return killCamOpen();
    },
    get killCamPlacement() {
      return settings.killCamPlacement();
    },
    get onKillCamMove() {
      return live.moveKillCam;
    },
    get onKillCamCommit() {
      return live.commitKillCam;
    },
    onKillCamClose: () => scene?.dismissKillCam(),
    get children() {
      return flyChrome({
        target,
        debrief: debrief(),
        recording,
        bestMission,
        map,
        mission,
        escMenu,
        onLeave: () => navigate(target.exitPath)
      });
    }
  });
}

/**
 * `/fly`'s own chrome inside the shared flight surface: the way out, the
 * debriefing, the Mission Recorder's panel and the map sheet.
 *
 * A function rather than a component, and here rather than in a file of its
 * own, because it is not reusable and has no state — it is the tail of `Fly`'s
 * own JSX, lifted out to keep that function inside the length gate. Everything
 * it needs is passed; nothing it does is decided here.
 */
function flyChrome(props) {
  return [_$createComponent(A, {
    "class": "flight__exit",
    get href() {
      return props.target.exitPath;
    },
    get children() {
      return ["← ", _$memo(() => props.target.exitLabel)];
    }
  }), _$createComponent(FlyDebrief, {
    get debrief() {
      return props.debrief;
    },
    get target() {
      return props.target;
    },
    get recording() {
      return props.recording;
    },
    get bestMission() {
      return props.bestMission;
    },
    get onContinue() {
      return props.onLeave;
    }
  }), _$createComponent(FlightRecorderOverlay, {
    get recording() {
      return props.recording;
    }
  }), _$createComponent(FlightMap, {
    get session() {
      return props.map;
    },
    get mission() {
      return props.mission;
    }
  }), _$createComponent(FlightEscMenu, {
    get session() {
      return props.escMenu;
    }
  })];
}